// ── The Helm: one nav grammar, ONE renderer (SPEC_nav_helm.md, D6; rail retired 2026-09-20) ──
// NAV_DOORS is the single source of truth for the app's doors — icons, order and
// names. It has exactly one renderer, the pill (<Helm />), at every width: on
// desktop the app is a phone-width column and the helm lives inside it. The
// wide rail (<Rail />) was retired 2026-09-20 — a nav rail outside a framed
// column is a second navigation for the same four doors. The same array is
// meant to become the fleet nav grammar for the other Our___ apps, so keep it
// data, not JSX — and "one renderer, helm at every width" is now the grammar
// they inherit.
//
// `view` values are the existing tab state values in App.js: Browse has always
// been "input" and Shop "list" — do not "tidy" them; every view === "…" branch
// in App.js keys on them.
//
// D3 (SPEC_rum_dxa_exposure.md): each door also has a HASH — display grammar for
// the URL bar and Splunk RUM, never a state rename. Navigation writes
// location.hash; a hashchange listener in App.js maps it back to `view`, so
// reload and Back land on the right door and the RUM agent's route-change
// instrumentation sees four first-class pages. Sheets and modals get no hash;
// the one exception is Wrap up (WRAP_UP_HASH) so the funnel's last step is a
// page view.
//
// DRIFT RISK — prod click-text unmask list (src/rum.js CHROME_ALLOW_LIST). On
// prod, text inside `.helm-label`, `.helm-plus` and the other chrome classes
// listed there is sent to Splunk in the clear. Any future chrome that renders
// household text (a household-name pill in the helm, member initials, an item
// count is fine but a name is not) must NOT reuse those classes or `op-chrome`.

import { useEffect, useState } from "react";

// Icons: the three existing tab glyphs lifted verbatim (same paths, currentColor)
// plus the new Home house outline from the mockup (1.6 stroke).

export function HomeIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

// Plan — horizon icon (was the Plan tab's SVG).
export function PlanIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 14" fill="none" aria-hidden="true">
      <circle cx="9" cy="5" r="2" fill="currentColor" />
      <line x1="9" y1="1" x2="9" y2="0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12.5" y1="2.5" x2="13.5" y2="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="5.5" y1="2.5" x2="4.5" y2="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="14" y1="5" x2="15.5" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="4" y1="5" x2="2.5" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M1 9 Q9 4 17 9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <line x1="0" y1="11" x2="18" y2="11" stroke="currentColor" strokeWidth="0.75" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

// Browse — grid icon (was the Browse tab's SVG).
export function BrowseIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

// Shop — basket icon (was the Shop tab's SVG).
export function ShopIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M6 7 Q6 3 9 3 Q12 3 12 7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M2 7 L3.5 15 Q5 16.5 9 16.5 Q13 16.5 14.5 15 L16 7 Z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
      <line x1="2.5" y1="10.5" x2="15.5" y2="10.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

// D7: four doors from day one. Home routes to a placeholder until HOME v1 —
// the pill's shape is what households learn, and adding a door later shifts
// every other door under the thumb.
export const NAV_DOORS = [
  { key: "home",   label: "Home",   view: "home",  hash: "#/home",   Icon: HomeIcon },
  { key: "plan",   label: "Plan",   view: "plan",  hash: "#/plan",   Icon: PlanIcon },
  { key: "browse", label: "Browse", view: "input", hash: "#/browse", Icon: BrowseIcon },
  { key: "shop",   label: "Shop",   view: "list",  hash: "#/shop",   Icon: ShopIcon, badge: true },
];

// D3: the Wrap up modal is the one non-door surface with a hash — the funnel's
// last step must be a page view. Nested under Shop so viewForHash still
// resolves it to the Shop door on reload.
export const WRAP_UP_HASH = "#/shop/wrap-up";

// view → hash ("list" → "#/shop"); null for a view with no door.
export function hashForView(view) {
  const door = NAV_DOORS.find((d) => d.view === view);
  return door ? door.hash : null;
}

// hash → view ("#/shop", "#/shop/wrap-up", "#shop" → "list"); null when the
// hash names no door (empty, an invite/ref-stripped URL, or anything foreign).
export function viewForHash(hash) {
  const seg = (hash || "").replace(/^#\/?/, "").split("/")[0].toLowerCase();
  const door = NAV_DOORS.find((d) => d.key === seg);
  return door ? door.view : null;
}

// Desktop column (2026-09-20 — mockup_desktop_phone_frame.html option A +
// mockup_desktop_background_treatment.html treatment 3). Past COLUMN_MIN_WIDTH
// the app becomes a phone-width column, centred, with a soft radius and shadow,
// floating on the household photo (scrim 44%, blur 7px); with no photo, the
// cream field. Below it NOTHING changes. The threshold is the old rail
// threshold, inherited rather than invented: 700px leaves a 135px gutter each
// side of a 430px column. Both are consumed by CSS only — there is no JS media
// query any more, because there is only one nav renderer to mount.
export const COLUMN_MIN_WIDTH = 700;   // was RAIL_MIN_WIDTH — same value, new job
export const COLUMN_MAX_WIDTH = 430;

// D9′ (v2, amended 2026-09-12) — the pill is compact EXACTLY when the door's
// header controls are off-screen. Position-based, not direction-based: the
// v2 direction rule let a small upward scroll restore the full pill mid-list,
// leaving no + on screen while the header row was still out of sight.
//
// `controlRow` is the door's control row element (Shop: the [count] [lens] [+]
// [Wrap up] row; Browse: a sentinel at the bottom of the search + chips block —
// the search bar itself is sticky and never leaves; Plan: a sentinel where the
// lens row will sit), or null when the door has none (Home never compacts).
// Compact once the row's bottom is `margin` px above the viewport top; full
// again once it is `margin` px back inside — an 8px band of hysteresis, no
// direction tracking, no deadband. rAF-throttled; recomputed on scroll, on
// resize and whenever the row element changes (door switch). Negative offsets
// (iOS rubber-band) only ever move the row DOWN, so they can't compact it.
export function useScrollCompact(controlRow, { margin = 8 } = {}) {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!controlRow) { setCompact(false); return undefined; }
    let ticking = false;
    let compactNow = null;
    const set = (v) => { if (v !== compactNow) { compactNow = v; setCompact(v); } };
    const update = () => {
      ticking = false;
      const bottom = controlRow.getBoundingClientRect().bottom;
      if (bottom < -margin) set(true);
      else if (bottom > margin) set(false);
      else if (compactNow === null) set(false);   // first read inside the band: full
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    update();
    // Capture-phase on the document, not window: scroll events do not bubble,
    // and on desktop (2026-09-20) the scroll root is the phone column's inner
    // scroller, not the document. Capture catches both.
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, [controlRow, margin]);
  return compact;
}
